import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit, ViewChild, AfterViewInit } from '@angular/core';
import { Location } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { FormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Country, GlobalMessageService, GlobalMessageType, OccSiteAdapter, Region, RoutingService } from '@spartacus/core';
import { CustomFormValidators } from '@spartacus/storefront';
import { Observable } from 'rxjs';
import {
  KnBrStaffRow,
  Status,
} from '../../../../feature-libs/kn-br-organization/administration/components/shared/kn-br-staff-customer-tabs/kn-br-staff-customer.models';
import { UserAccountService } from '../../../../feature-libs/kn-br-user/kn-br-account/core/facade/user-account.service';
import { AtgUserManagementService } from '../atg-user-management.service';
import { AtgAddEditUsersListComponent } from '../atg-add-edit-users-list/atg-add-edit-users-list.component';
import { debounceTime, finalize, shareReplay, startWith, take, tap } from 'rxjs/operators';

const ACCOUNT_STATUS_ACTIVE_EMAIL = 'ACTIVE_EMAIL';

@Component({
  selector: 'atg-edit-user',
  templateUrl: './atg-edit-user.component.html',
  styleUrl: './atg-edit-user.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AtgEditUserComponent implements OnInit, AfterViewInit {
  isAdmin = false; // TODO: derive from auth/user-group service
  isSaving = false;
  isResettingPassword = false;
  selectedAccountsPayload: any[] = [];
  showNewPassword = false;
  showConfirmPassword = false;
  accounts: { name: string; accountNumber: string; status: string }[] = [];
  parentSoldToList: any[] = [];

  @ViewChild(AtgAddEditUsersListComponent) private listRef!: AtgAddEditUsersListComponent;

  editUserForm!: UntypedFormGroup;
  passwordForm!: UntypedFormGroup;

  private _user: KnBrStaffRow | null = null;

  @Input()
  set user(value: KnBrStaffRow | null) {
    this._user = value;
    if (value) {

      this.parentSoldToList = (value as any).parentSoldToList || [];

      if (this.parentSoldToList && this.parentSoldToList.length > 0) {
        this.selectedAccountsPayload = this.parentSoldToList as any[];
      }
      this.cdr.markForCheck();
      if (this.listRef) {
        this.listRef.loadParentSoldToList();
      } else {
        console.log('[EditUser] listRef is not available yet');
      }
      if (this.editUserForm) {
        this.patchForm(value);
      }
    }
  }

  get user(): KnBrStaffRow | null {
    return this._user;
  }

  accountStatusOptions: any[] = [];
  isLoadingAccountStatuses = false;
  userRoleOptions: any[] = [];
  isLoadingUserRoles = false;

  dateFormatOptions = [
    { value: 'MM/dd/yyyy', label: 'MM/DD/YYYY' },
    { value: 'dd/MM/yyyy', label: 'DD/MM/YYYY' },
    { value: 'yyyy/MM/dd', label: 'YYYY/MM/DD' },
  ];

  languageOptions = [
    { value: 'en', label: 'English' },
    { value: 'fr_CA', label: 'Canadian French' },
  ];

  countries$: Observable<Country[]> | null = null;
  isCountryLoading = false;
  states$: Observable<Region[]> | null = null;
  isStateLoading = false;

  constructor(
    private fb: FormBuilder,
    private routingService: RoutingService,
    private userManagementService: AtgUserManagementService,
    private currentUserService: UserAccountService,
    private occSiteAdapter: OccSiteAdapter,
    private cdr: ChangeDetectorRef,
    private globalMessageService: GlobalMessageService,
    private location: Location,
  ) {}

  ngOnInit(): void {
    this.buildForm();
    this.initializeAdminAccess();
    this.loadUserRoles();
    this.loadAccountStatuses();
    this.loadCountries();
    this.subscribeToCountryChanges();
    // Input setter fires before ngOnInit, so patch here once the form exists
    if (this._user) {
      this.patchForm(this._user);
    }
  }

  private loadCountries(): void {
    this.isCountryLoading = true;
    this.countries$ = this.occSiteAdapter.loadCountries().pipe(
      tap(() => {
        this.isCountryLoading = false;
        this.cdr.markForCheck();
      }),
      shareReplay(1)
    );
  }

  private subscribeToCountryChanges(): void {
    this.editUserForm.get('country')?.valueChanges
      .pipe(
        startWith(this.editUserForm.get('country')?.value),
        debounceTime(300)
      )
      .subscribe((countryIsoCode: string) => {
        this.loadStatesForCountry(countryIsoCode);
      });
  }

  private loadStatesForCountry(countryIsoCode: string): void {
    if (countryIsoCode) {
      this.isStateLoading = true;
      this.states$ = this.occSiteAdapter.loadRegions(countryIsoCode).pipe(
        tap((regions) => {
          this.isStateLoading = false;
          this.reconcileStateValue(regions);
          this.cdr.markForCheck();
        }),
        shareReplay(1)
      );
    } else {
      this.states$ = null;
    }
    // On OnPush, this method usually runs from an async (debounced) callback;
    // without this, the view never notices states$ changed, so the async pipe
    // never subscribes and loadRegions() is never actually triggered.
    this.cdr.markForCheck();
  }

  // Existing users may have a plain code (e.g. "AL") while ng-select's items use the full isocode (e.g. "US-AL")
  private reconcileStateValue(regions: Region[]): void {
    const stateControl = this.editUserForm.get('state');
    const currentValue = stateControl?.value;

    if (!currentValue || typeof currentValue !== 'string' || regions.some((r) => r.isocode === currentValue)) {
      return;
    }

    const match = regions.find((r) => r.isocode?.endsWith(`-${currentValue}`));
    if (match) {
      stateControl?.setValue(match.isocode, { emitEvent: false });
    }
  }

  onCountryChanged(): void {
    this.editUserForm.get('state')?.reset('', { emitEvent: false });
  }

  private normalizeStateCode(stateValue: string): string {
    if (!stateValue) {
      return '';
    }

    const trimmed = stateValue.trim();

    // Region isocode can come as US-AL; API expects AL.
    if (trimmed.includes('-')) {
      const shortCode = trimmed.split('-').pop()?.trim();
      return shortCode || trimmed;
    }

    return trimmed;
  }

  ngAfterViewInit(): void {
    if (this.parentSoldToList && this.parentSoldToList.length > 0 && this.listRef) {
      this.listRef.loadParentSoldToList();
      this.cdr.markForCheck();
    }
  }

  private initializeAdminAccess(): void {
    this.currentUserService
      .get()
      .pipe(take(1))
      .subscribe((user: any) => {
        const roles: string[] = Array.isArray(user?.roles) ? user.roles : [];
        this.isAdmin = roles.some((role) => role?.toLowerCase().includes('admingroup'));
        this.updateAdminEditableFields();
        this.cdr.markForCheck();
      });
  }

  private updateAdminEditableFields(): void {
    const adminOnlyFields = ['accountStatus', 'userRole', 'salesDistrict', 'salesGroup', 'salesOffice', 'createOrder'];

    adminOnlyFields.forEach((field) => {
      const control = this.editUserForm.get(field);
      if (!control) {
        return;
      }

      if (this.isAdmin) {
        control.enable({ emitEvent: false });
      } else {
        control.disable({ emitEvent: false });
      }
    });
  }

  private loadUserRoles(): void {
    this.isLoadingUserRoles = true;
    this.userManagementService.getAvailableUserTypes()
      .pipe(
        finalize(() => {
          this.isLoadingUserRoles = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: (userTypes: any[]) => {
          this.userRoleOptions = userTypes.map((ut) => ({
            label: this.formatUserTypeLabel(ut.value),
            value: ut.value,
          }));
          this.cdr.markForCheck();
        },
        error: (err) => {
          console.error('Error loading user types:', err);
          this.userRoleOptions = [];
        },
      });
  }

  private formatUserTypeLabel(value: string): string {
    return value
      .split('_')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
      .join(' ');
  }

  private loadAccountStatuses(): void {
    this.isLoadingAccountStatuses = true;
    this.userManagementService.getAvailableAccountStatuses()
      .pipe(
        finalize(() => {
          this.isLoadingAccountStatuses = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: (statuses: any[]) => {
          this.accountStatusOptions = statuses;
          this.cdr.markForCheck();
        },
        error: (err) => {
          console.error('Error loading account statuses:', err);
          this.accountStatusOptions = [];
        },
      });
  }

  private buildForm(): void {
    this.editUserForm = this.fb.group({
      accountStatus:        [{ value: '', disabled: !this.isAdmin }],
      name:                 [''],
      email:                [{ value: '', disabled: true }],
      userCreatedOn:        [{ value: '', disabled: true }],
      accountActivatedDate: [{ value: '', disabled: true }],
      activatedBy:          [{ value: '', disabled: true }],
      lastLoginDate:        [{ value: '', disabled: true }],
      userRole:             [{ value: '', disabled: !this.isAdmin }],
      dateFormat:           [''],
      phoneNumber:          ['', [Validators.pattern(/^\d+$/), Validators.minLength(7), Validators.maxLength(10)]],
      emailSentDate:        [{ value: '', disabled: true }],
      emailSentBy:          [{ value: '', disabled: true }],
      language:             ['en'],
      addressLine1:         [''],
      addressLine2:         [''],
      city:                 [''],
      state:                [''],
      postalCode:           [''],
      country:              [''],
      createOrder:          [{ value: false, disabled: !this.isAdmin }],
    });

    this.passwordForm = this.fb.group({
      newPassword:     ['', [Validators.required, ...CustomFormValidators.passwordValidators]],
      confirmPassword: ['', [Validators.required, ...CustomFormValidators.passwordValidators]],
    });
  }

  private patchForm(user: KnBrStaffRow): void {
    const resolvedDateFormat = this.resolveDateFormatValue((user as any).dateFormat);

    this.editUserForm.patchValue({
      name:                 user.name || `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim(),
      email:                user.email ?? '',
      accountStatus:        user.accountStatus ?? this.mapStatus(user.status),
      userRole:             this.resolveUserRoleValue(user),
      phoneNumber:          this.resolvePhoneNumber(user),
      userCreatedOn:        user.userCreatedOn ?? '',
      accountActivatedDate: user.accountActivatedDate ?? '',
      activatedBy:          user.activatedBy ?? '',
      lastLoginDate:        user.lastLoginDate ?? '',
      dateFormat:           resolvedDateFormat,
      createOrder:          user.createOrder ?? false,
      emailSentDate:        user.emailSentDate ?? '',
      emailSentBy:          user.emailSentBy ?? '',
      language:             (user as any).language?.isocode || 'en',
      addressLine1:         (user as any).addressLine1 || (user as any).address1 || '',
      addressLine2:         (user as any).addressLine2 || (user as any).address2 || '',
      city:                 (user as any).city ?? '',
      state:                (user as any).state?.isocode || (user as any).state || (user as any).region || '',
      postalCode:           (user as any).postalCode || (user as any).zipCode || '',
      country:              (user as any).country?.isocode || (user as any).country || '',
    });
  }

  private resolveDateFormatValue(value?: string): string {
    const order = (value?.match(/[a-zA-Z]+/g) || []).map((part) => part[0].toUpperCase()).join('/');
    const normalizedIncoming =
      order === 'D/M/Y' ? 'DD/MM/YYYY' :
      order === 'Y/M/D' ? 'YYYY/MM/DD' :
      'MM/DD/YYYY';
    const matched = this.dateFormatOptions.find(
      (opt) => opt.value.toUpperCase().replace(/\s+/g, '') === normalizedIncoming
    );

    return matched?.value ?? this.dateFormatOptions[0].value;
  }

  private resolvePhoneNumber(user: KnBrStaffRow): string {
    const rawPhone =
      (user as any).phoneNumber ??
      (user as any).businessPhone ??
      (user as any).mobileNumber ??
      user.knbrMobileNumber ??
      '';

    return String(rawPhone).replace(/\D/g, '');
  }

  private normalizeRole(value?: string): string {
    if (!value) {
      return '';
    }

    return value.trim().toUpperCase().replace(/[\s-]+/g, '_');
  }

  private resolveUserRoleValue(user: KnBrStaffRow): string {
    // Use userRole directly from API if available
    const userRole = this.normalizeRole((user as any).userRole);
    if (userRole) {
      return userRole;
    }

    // Fallback to role-based mapping only if userRole is not available
    return this.mapRoles(user.roles);
  }

  private mapStatus(status: Status): string {
    const map: Partial<Record<Status, string>> = {
      [Status.Active]:   'ACTIVE',
      [Status.Inactive]: 'INACTIVE',
    };
    return map[status] ?? 'ACTIVE';
  }

  private mapRoles(roles?: string[]): string {
    if (!roles?.length) return '';

    if (roles.includes('atg-no-pricing-group') && roles.includes('b2bpurchasingagentcustomergroup')) {
      return 'DISTRIBUTOR_USER_NO_PRICING';
    }
    if (roles.includes('b2badmingroup') || roles.includes('b2bpurchasingagentadmingroup')) {
      return 'DISTRIBUTOR_ADMIN';
    }
    if (roles.includes('b2bpurchasingagentcustomergroup') || roles.includes('step2customergroup')) {
      return 'DISTRIBUTOR_USER';
    }

    return '';
  }

  onResetPassword(): void {
    const newPwd     = this.passwordForm.get('newPassword');
    const confirmPwd = this.passwordForm.get('confirmPassword');

    this.passwordForm.markAllAsTouched();
    if (this.passwordForm.invalid) return;

    if (newPwd!.value !== confirmPwd!.value) {
      confirmPwd!.setErrors({ passwordMismatch: true });
      return;
    }

    if (!this._user?.id) return;

    const payload = { new: newPwd!.value };

    this.isResettingPassword = true;
    this.userManagementService.changePassword(this._user.email ?? '', payload)
      .pipe(finalize(() => {
        this.isResettingPassword = false;
        this.cdr.markForCheck();
      }))
      .subscribe({
        next: () => {
          this.passwordForm.reset();
          this.globalMessageService.add(
            { raw: 'Password updated successfully.' },
            GlobalMessageType.MSG_TYPE_CONFIRMATION
          );
        },
        error: (err) => {
          console.error('Failed to change password:', err);
          if (err instanceof HttpErrorResponse) {
            const errorDetail = err.error?.errors?.[0];
            if (
              errorDetail?.type === 'ValidationError' ||
              (errorDetail?.type === 'PasswordPolicyViolationError' && errorDetail?.reason === 'historical')
            ) {
              this.globalMessageService.remove(GlobalMessageType.MSG_TYPE_ERROR);
              this.globalMessageService.add(
                { key: 'updatePasswordForm.passwordHistoryError' },
                GlobalMessageType.MSG_TYPE_ERROR
              );
              return;
            }
          }
          this.globalMessageService.add(
            { raw: 'Failed to update password. Please try again.' },
            GlobalMessageType.MSG_TYPE_ERROR
          );
        },
      });
  }

  onCancelPassword(): void {
    this.passwordForm.reset();
  }

  onAccountsPayloadChange(payload: any[]): void {
    this.selectedAccountsPayload = payload ?? [];
    this.cdr.markForCheck();
  }

  goToReviewAccounts(): void {
    this.userManagementService.setAddUser();
    this.routingService.go({ cxRoute: 'atgAddUser' }, { queryParams: { mode: 'add' } });
  }

  onRequestAccountAccess(): void {
    this.routingService.go({ cxRoute: 'requestAccountAccess' });
  }

  onSave(): void {
    if (this.editUserForm.valid && this._user?.id) {
      const selectedAccountsPayload = this.listRef?.getPayload() ?? [];
      const rawValue = this.editUserForm.getRawValue();

      if (rawValue.accountStatus === ACCOUNT_STATUS_ACTIVE_EMAIL && !selectedAccountsPayload.length) {
        this.globalMessageService.add(
          { key: 'atgUserManagement.editUser.accountSelectionRequired' },
          GlobalMessageType.MSG_TYPE_ERROR
        );
        this.cdr.markForCheck();
        return;
      }

      this.isSaving = true;
      const formData = {
        ...rawValue,
        state: this.normalizeStateCode(rawValue.state),
        parentSoldToList: selectedAccountsPayload,
        email: this.editUserForm.get('email')?.value || this._user?.email,
        accountStatus: this.editUserForm.get('accountStatus')?.value,
      };

      this.userManagementService.editUser(this._user.id, formData)
        .pipe(finalize(() => {
          this.isSaving = false;
          this.cdr.markForCheck();
        }))
        .subscribe({
          next: () => {
            this.globalMessageService.add(
              { raw: 'User information has been updated successfully.' },
              GlobalMessageType.MSG_TYPE_CONFIRMATION
            );
          },
          error: (err) => {
            console.error('Failed to save user:', err);
            this.globalMessageService.add(
              { raw: 'Failed to update user information. Please try again.' },
              GlobalMessageType.MSG_TYPE_ERROR
            );
          },
        });
    } else {
      this.editUserForm.markAllAsTouched();
    }
  }

  onCancel(): void {
    this.routingService.go(['/users']);
  }

  goBack(): void {
    this.location.back();
  }
}
